/**
 * Where the window scrolls when the site moves to another page (public-site R13, F-PUB6).
 *
 * The site is one React page switched by `?page=…`, so a link (the footer's Privacy Policy, the
 * navbar) kept the scroll position of the page it was clicked on — Privacy opened at scrollY 2222,
 * the footer of the home page. A link now opens the new page at its top, or at its section when the
 * link names one (`?page=terms#data-sources`); back / forward and the first load keep the browser's
 * own position (`POP`).
 */

/** React Router's navigation type: a link or `navigate()` is PUSH / REPLACE, back / forward and the first load POP. */
export type NavigationKind = 'POP' | 'PUSH' | 'REPLACE';

export type ScrollTarget = { kind: 'none' } | { kind: 'top' } | { kind: 'anchor'; id: string };

/** What a navigation of `navigationType` to a location with `hash` ('' or '#id') does to the scroll position. */
export function scrollTargetOnNavigate(navigationType: NavigationKind | string, hash: string): ScrollTarget {
  if (navigationType === 'POP') return { kind: 'none' };
  const raw = (hash ?? '').replace(/^#/, '');
  if (!raw) return { kind: 'top' };
  let id = raw;
  try {
    id = decodeURIComponent(raw);
  } catch {
    // a malformed escape ("#%E0") — use the hash as written
  }
  return { kind: 'anchor', id };
}

/** Applies a target: the section into view, else (no such element) the top; `none` leaves the window alone. */
export function applyScrollTarget(
  target: ScrollTarget,
  win: Pick<Window, 'scrollTo'>,
  doc: Pick<Document, 'getElementById'>,
): void {
  if (target.kind === 'none') return;
  if (target.kind === 'anchor') {
    const el = doc.getElementById(target.id);
    if (el) {
      el.scrollIntoView();
      return;
    }
  }
  win.scrollTo(0, 0);
}
